import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  makeStyles,
} from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canoeEquipementOf,
  countBookingPeople,
  fixedDayjs,
  labelOfArrival,
  numberOrZero,
} from '@crm/utils';
import { CrmTableRow, SmallTableCell } from './Table.component';
import { PositiveNumberField } from './fields/PositiveNumberField.component';
import { Dayjs } from 'dayjs';
import { get, post, put } from '../fetch';
import { useDomainPath } from '../auth/auth.context';

const useStyles = makeStyles((theme: Theme) => ({
  cellNoPadding: {
    padding:'0'
  },
  cellNoPaddingWarning: {
    padding:'0',
    color: 'red',
    fontSize: '0.7rem',
    fontWeight: "bold"
  },
  cellPaddingLeft: {
    padding: '0 0 4px 16px'
  },
  cellTopPadding: {
    padding: '0.5rem 0 0 0'
  },
}));

/**
 * Interface pour les props du composant DayDetailsTable
 */
interface Props {
  /** Liste des réservations à afficher */
  reservations: CRM.ReservationOverview[];
  /** Clé de date pour déterminer le type de date ('activityArrivalDate' ou 'activityDate') */
  dateKey: 'activityArrivalDate' | 'activityDate';
  /** Date pour laquelle afficher les détails */
  date: Dayjs;
  /** ID du site d'activité */
  spotId: string;
  /** Indique si on affiche les détails d'arrivée */
  isArrivalDetails?: boolean;
}

/**
 * Interface pour les détails d'équipements et personnes
 */
interface Details {
  /** Nombre total de personnes */
  people: number;
  /** Nombre de bateaux C2 (2 places) */
  c2: number;
  /** Nombre de bateaux C3 (3 places) */
  c3: number;
  /** Nombre de bateaux K1 (1 place) */
  k1: number;
}

/**
 * Type pour regrouper les détails par label d'arrivée
 */
type DetailsByArrivalLabel = CRM.Dictionary<Details>;

/**
 * Fusionne les détails d'une réservation avec les détails existants
 * 
 * @param detail Détails existants à fusionner
 * @param reservation Réservation à traiter
 * @param isArrivalDetails Indique si on traite les détails d'arrivée
 * @returns Détails fusionnés
 */
function mergeDetails(
  detail: Details,
  reservation: CRM.ReservationOverview,
  isArrivalDetails?: boolean
): Details {
  const people =
    (isArrivalDetails && reservation.options?.peopleAtArrivalCount !== undefined
      && reservation.options?.peopleAtArrivalCount !== null
      && reservation.options?.peopleAtArrivalCount >= 0) ?
      reservation.options?.peopleAtArrivalCount
    : countBookingPeople(reservation.people, reservation.options.instructors);
  const c2 = canoeEquipementOf('c2', reservation.options);
  const c3 = canoeEquipementOf('c3', reservation.options);
  const k1 = canoeEquipementOf('k1', reservation.options);

  return {
    people: (detail?.people || 0) + people,
    c2: (detail?.c2 || 0) + numberOrZero(c2),
    c3: (detail?.c3 || 0) + numberOrZero(c3),
    k1: (detail?.k1 || 0) + numberOrZero(k1),
  };
}

/**
 * Composant de tableau des détails quotidiens des réservations
 * 
 * Ce composant affiche :
 * - Les détails des réservations par créneau horaire
 * - Le calcul des équipements nécessaires (C2, C3, K1)
 * - La gestion du stock de bateaux avec édition
 * - Les totaux par jour et par équipement
 * 
 * Logique métier :
 * - Calcul automatique des équipements selon les réservations
 * - Gestion du stock initial et final de bateaux
 * - Édition en ligne du stock avec validation
 * - Affichage des alertes de stock insuffisant
 * - Regroupement par créneaux horaires d'arrivée/départ
 * 
 * @param reservations Liste des réservations à afficher
 * @param dateKey Clé de date ('activityArrivalDate' ou 'activityDate')
 * @param date Date pour laquelle afficher les détails
 * @param spotId ID du site d'activité
 * @param isArrivalDetails Indique si on affiche les détails d'arrivée
 * @returns Composant React du tableau des détails quotidiens
 */
export const DayDetailsTable: React.FC<Props> = ({ dateKey, reservations, isArrivalDetails, date, spotId }) => {

  const classes = useStyles();
  
  /**
   * Stock de bateaux par défaut pour la date et le site
   */
  const defaultBoatStock: CRM.BoatStockDocument = useMemo(() => {
    return {
      _id: '',
      date: fixedDayjs(date.toDate().toISOString()).endOf('day').toDate(),
      spotId: spotId,
      c2InitialStock: 0,
      k1InitialStock: 0,
      c2FinalStock: 0,
      k1FinalStock: 0
    }
  },[
    date,
    spotId,
  ]);
  
  const [ boatStock, setBoatStock ] = useState<CRM.BoatStockDocument>(defaultBoatStock);
  const [ editBoatStock, setEditBoatStock ] = useState<boolean>(false);
  const [ c2Reservations, setC2Reservations] = useState<number | undefined>(undefined);
  const [ k1Reservations, setK1Reservations] = useState<number | undefined>(undefined);
  const boatStockPath = useDomainPath('/boat-stock');

  /**
   * Record d'un jour passé → figé, non éditable.
   * Règle métier : le stock physique de la journée est arrêté à minuit. On ne
   * réécrit jamais l'historique.
   */
  const isPastDay = useMemo(() => {
    const today = fixedDayjs(new Date()).startOf('day');
    return fixedDayjs(date.toDate().toISOString()).startOf('day').isBefore(today, 'day');
  }, [date]);

  /**
   * Crée ou met à jour le stock de bateaux
   * 
   * @param value Valeurs partielles à mettre à jour
   * @param refresh Indique si on doit rafraîchir après la mise à jour
   */
  const createOrUpdateBoatStock = useCallback((value:Partial<CRM.BoatStock>, refresh: boolean = true) => {

    const newBoatStockDocument = {...boatStock, ...value};
    const {_id, ...newBoatStock} = newBoatStockDocument;
    if(boatStock._id !== '') {
      put<CRM.BoatStock>(boatStockPath+'/'+boatStock._id, 
          newBoatStock as CRM.BoatStock
        ).then(() => {
        refresh === false && setBoatStock(newBoatStockDocument);
        refresh && refreshBoatStock();
      });
    } else {
      post<CRM.BoatStock>(boatStockPath, 
          newBoatStock as CRM.BoatStock
        ).then((result: CRM.BoatStockDocument) => {
          if(result._id) {
            refresh === false && setBoatStock(result);
            refresh && refreshBoatStock();
            return result._id;
          }
      });
    }
    // #TODO fix eventually these dependancies for not causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    boatStockPath,
    boatStock
  ]);

  /**
   * Calcule les détails des réservations par créneau horaire
   */
  const dayDetails = useMemo(() => {

    return reservations.reduce((details, reservation) => {
      const label = labelOfArrival(reservation[dateKey]);
      details[label] = mergeDetails(details[label], reservation, isArrivalDetails);
      return details;
    }, {} as DetailsByArrivalLabel);
  }, [reservations, dateKey, isArrivalDetails]);


  /**
   * Calcule les totaux de la journée
   */
  const dayTotal = useMemo(() => {
    return Object.values(dayDetails).reduce(
      (total, arrivalDetails) => {
        total.people += arrivalDetails.people;
        total.c2 += arrivalDetails.c2;
        total.c3 += arrivalDetails.c3;
        total.k1 += arrivalDetails.k1;
        return total;
      },
      { people: 0, c2: 0, c3: 0, k1: 0 } as Details,
    );
  }, [dayDetails]);

  /**
   * Rafraîchit le stock de bateaux depuis le serveur
   */
  const refreshBoatStock = useCallback(() => {
    get<CRM.BoatStockReservations>(
      boatStockPath+'/by/dateSpot?date='+date.format('YYYYMMDD') +
      '&spotId=' + spotId
      ).then((boatStockResult) => {
        setK1Reservations(boatStockResult.k1ReservationStock);
        setC2Reservations(boatStockResult.c2ReservationStock)
        if(boatStockResult.boatStock && boatStockResult.boatStock.spotId/*&& boatStockResult._id*/) {
          setBoatStock(boatStockResult.boatStock);
          //createOrUpdateBoatStock(boatStockResult.boatStock, false);
          
        } /*else {
          setBoatStock({
            ...defaultBoatStock, 
            c2InitialStock: dayTotal.c2, 
            k1InitialStock: dayTotal.k1
          });
        }*/
      });
  }, [
    spotId,
    date,
    boatStockPath,
    //dayTotal,
    //defaultBoatStock
  ]);

  useEffect(() => {
    refreshBoatStock();
  },[
    refreshBoatStock
  ]);

  return (
    <TableContainer component={Paper}>
      <Table>
        {/*<TableHead>
          <TableRow>
                <TableCell className={classes.cellTopPadding} colSpan={2} align="center">Stock initial :</TableCell>
                <TableCell className={classes.cellPaddingLeft} align="center">
                  <PositiveNumberField
                    label="C2"
                    variant="standard"
                    value={boatStock.c2InitialStock}
                    minValue={dayTotal.c2}
                    onChange={(el)=> {createOrUpdateBoatStock({c2InitialStock: Number(el.target.value)})}}
                    fullWidth={true}
                    required
                  />
                </TableCell>
                <TableCell className={classes.cellNoPadding} align="center"></TableCell>
                <TableCell className={classes.cellPaddingLeft} align="center">
                  <PositiveNumberField
                    label="K1"
                    variant="standard"
                    value={boatStock?.k1InitialStock}
                    minValue={dayTotal.k1}
                    onChange={(el)=> {createOrUpdateBoatStock({k1InitialStock: Number(el.target.value)})}}
                    fullWidth={true}
                    required
                  />
                </TableCell>
            </TableRow>
          </TableHead>*/}
        <TableHead>
          <TableRow>
            <SmallTableCell align="center">Horaire</SmallTableCell>
            <SmallTableCell align="center">Personnes</SmallTableCell>
            <SmallTableCell align="center">C2</SmallTableCell>
            <SmallTableCell align="center">C3</SmallTableCell>
            <SmallTableCell align="center">K1</SmallTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(dayDetails).map(([label, details]) => (
            <CrmTableRow key={label}>
              <SmallTableCell align="center">{label}</SmallTableCell>
              <SmallTableCell align="center">{details.people}</SmallTableCell>
              <SmallTableCell align="center">{details.c2}</SmallTableCell>
              <SmallTableCell align="center">{details.c3}</SmallTableCell>
              <SmallTableCell align="center">{details.k1}</SmallTableCell>
            </CrmTableRow>
          ))}
          <CrmTableRow>
            <SmallTableCell align="center">
              <b>Total</b>
            </SmallTableCell>
            <SmallTableCell align="center">
              <b>{dayTotal.people}</b>
            </SmallTableCell>
            <SmallTableCell align="center">
              <b>{dayTotal.c2}</b>
            </SmallTableCell>
            <SmallTableCell align="center">
              <b>{dayTotal.c3}</b>
            </SmallTableCell>
            <SmallTableCell align="center">
              <b>{dayTotal.k1}</b>
            </SmallTableCell>
          </CrmTableRow>
        </TableBody>
        <TableBody>
          <TableRow>
                <TableCell  className={classes.cellTopPadding} colSpan={2} align="center">Stock :</TableCell>
                <TableCell className={classes.cellPaddingLeft} align="center">
                  <PositiveNumberField
                    label="C2"
                    variant="standard"
                    value={boatStock.c2InitialStock + boatStock.c2FinalStock/*boatStock.c2FinalStock > -1 ? boatStock.c2FinalStock : (boatStock.c2InitialStock-dayTotal.c2)*/}
                    //minValue={0}
                    onChange={(el)=> {createOrUpdateBoatStock({c2FinalStock: Number(el.target.value)-boatStock.c2InitialStock})}}
                    fullWidth={true}
                    disabled={isPastDay || !editBoatStock}
                    required
                  />
                </TableCell>
                <TableCell className={classes.cellNoPadding} align="center"></TableCell>
                <TableCell className={classes.cellPaddingLeft} align="center">
                  <PositiveNumberField
                      label="K1"
                      variant="standard"
                      value={boatStock.k1InitialStock + boatStock.k1FinalStock/*boatStock.k1FinalStock > -1 ? boatStock.k1FinalStock : (boatStock.k1InitialStock-dayTotal.k1)*/}
                      //minValue={0}
                      onChange={(el)=> {createOrUpdateBoatStock({k1FinalStock: Number(el.target.value)-boatStock.k1InitialStock})}}
                      fullWidth={true}
                      disabled={isPastDay || !editBoatStock}
                      required
                    />
                </TableCell>
            </TableRow>
            <TableRow>
              <TableCell  className={classes.cellTopPadding} colSpan={2} align="center">
                { !isPastDay && !editBoatStock &&
                  <Button
                    fullWidth={false}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={()=>setEditBoatStock(true)}
                  >
                    Modifier le stock
                </Button>}
                { !isPastDay && editBoatStock &&
                  <Button
                    fullWidth={false}
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={()=>setEditBoatStock(false)}
                  >
                    Valider le stock
                </Button>}
              </TableCell>
              <TableCell className={classes.cellNoPaddingWarning} align="center">
              {
                //(boatStock.c2InitialStock + boatStock.c2FinalStock) < 0 && c2Reservations === undefined ? "(Manque "+Math.abs(boatStock.c2InitialStock + boatStock.c2FinalStock)+")" : ""
              }
              {
                /*c2Reservations !== undefined && (boatStock.c2InitialStock + boatStock.c2FinalStock < c2Reservations) ? 
                  "(Manque "+(c2Reservations - (boatStock.c2InitialStock + boatStock.c2FinalStock))+")" : ""*/

                  c2Reservations !== undefined && (-c2Reservations + (boatStock.c2InitialStock + boatStock.c2FinalStock) < 0) ? 
                  "(Manque "+Math.abs(-c2Reservations + (boatStock.c2InitialStock + boatStock.c2FinalStock))+")" : ""
              }
              </TableCell>
              <TableCell className={classes.cellNoPadding} align="center"></TableCell>
              <TableCell className={classes.cellNoPaddingWarning} align="center">
              {
                //boatStock.k1FinalStock < 0 ? "Manque "+Math.abs(boatStock.k1FinalStock)+")" : ""
              }
              {
                k1Reservations !== undefined && (-k1Reservations + (boatStock.k1InitialStock + boatStock.k1FinalStock) < 0) ? 
                  "(Manque "+Math.abs(-k1Reservations + (boatStock.k1InitialStock + boatStock.k1FinalStock))+")" : ""
              }
              </TableCell> 
            </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
